import React from "react";
import PropTypes from "prop-types";
import DatePicker from "react-datepicker";
import ReactSelect from "react-select";
import FilterContainer from "./FilterContainer";
import { DatePickerIcon, SearchIcon } from "../Icons/SvgIcon";
import "react-datepicker/dist/react-datepicker.css";

const SearchFilterBar = ({
  searchValue,
  onSearchChange,
  onSearchSubmit,
  searchPlaceholder = "Search...",
  filters = [],
  onReset,
  children,
}) => {
  const renderFilter = (filter) => {
    const { type, key, label, value, onChange, placeholder, options, onSubmit } = filter;

    if (type === "search") {
      return (
        <div key={key} className="lh-1">
          <label htmlFor={key} className="form-label">
            {label}
          </label>
          <div className="search-box">
            <input
              id={key}
              type="text"
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              className="search-input"
              placeholder={placeholder}
              onKeyDown={(e) => {
                if (e.key === "Enter" && onSubmit) {
                  onSubmit();
                }
              }}
            />
            <SearchIcon />
          </div>
        </div>
      );
    }

    if (type === "select") {
      return (
        <div key={key} className="lh-1">
          <label htmlFor={key} className="form-label">
            {label}
          </label>
          <ReactSelect
            id={key}
            name={key}
            classNamePrefix="custom-select"
            placeholder={placeholder || "Select option"}
            options={options}
            value={options?.find((opt) => opt.value === value)}
            onChange={(selectedOption) =>
              onChange(selectedOption ? selectedOption.value : null)
            }
            className="react-select-container search-filter-select"
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>
      );
    }

    if (type === "dateRange") {
      return (
        <div key={key} className="lh-1">
          <label htmlFor={key} className="form-label">
            {label}
          </label>
          <DatePicker
            selectsRange
            showIcon
            className="filter-box date-picker filter-date-picker-input"
            startDate={value?.start}
            endDate={value?.end}
            onChange={(dates) => {
              const [start, end] = dates;
              onChange({ start, end });
            }}
            placeholderText={placeholder || "Anytime"}
            monthsShown={2}
            popperProps={{ strategy: "fixed" }}
            icon={
              <DatePickerIcon className="react-datepicker__calendar-icon" />
            }
          />
        </div>
      );
    }

    return null;
  };

  const hasSearchFilter = filters.some(f => f.type === "search");

  return (
    <div className="mb-24 filter-container">
      {!hasSearchFilter && searchValue !== undefined && (
        <div className="lh-1">
          <label htmlFor="search" className="form-label">
            Search
          </label>
          <div className="search-box">
            <input
              id="search"
              type="text"
              value={searchValue || ""}
              onChange={(e) => onSearchChange(e.target.value)}
              className="search-input"
              placeholder={searchPlaceholder}
              onKeyDown={(e) => {
                if (e.key === "Enter" && onSearchSubmit) {
                  onSearchSubmit();
                }
              }}
            />
            <SearchIcon />
          </div>
        </div>
      )}
      {filters.map(renderFilter)}
      {children}
      <FilterContainer onReset={onReset} />
    </div>
  );
};

SearchFilterBar.propTypes = {
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  onSearchSubmit: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(["search", "select", "dateRange"]).isRequired,
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.any,
      onChange: PropTypes.func.isRequired,
      placeholder: PropTypes.string,
      options: PropTypes.array,
      onSubmit: PropTypes.func,
    })
  ),
  onReset: PropTypes.func.isRequired,
  children: PropTypes.node,
};

export default SearchFilterBar;
